import { useAppDispatch, useAppSelector } from 'app/hooks';
import { CondenserProductType, SystemOptionType } from 'data/types';
import {
  addPtacCustomSelection,
  removePtacCustomSelection
} from 'features/ptac-selection';
import { bumpCustomSystemsRevision } from 'features/systems';
import { useCallback } from 'react';
import { resolveCustomSystemName } from 'utils/custom-system-swap';
import {
  buildPtacCustomSelection,
  isPtacCustomOption,
  ptacCustomSelectionId,
  ptacCustomSystemNames
} from 'utils/ptac-custom-selections';
import { otherPtacUnitSizes } from 'utils/ptac-unit-swap';

export type UsePtacUnitSwapResult = {
  alternativesFor: (condenserUniqueId: string) => CondenserProductType[];
  swap: (condenserUniqueId: string, productId: number) => void;
};

export default function usePtacUnitSwap(
  option: SystemOptionType,
  onCustomSystemSaved?: (uniqueId: string) => void
): UsePtacUnitSwapResult {
  const dispatch = useAppDispatch();
  const alternatives = useAppSelector(
    (state) => state.ptacSelection.systems.alternativesByOptionId[option.id]
  );
  const unitPicks = useAppSelector(
    (state) => state.ptacSelection.systems.unitPicksByOptionId[option.id]
  );
  const ptacOptions = useAppSelector(
    (state) => state.ptacSelection.systems.options
  );
  const systemsToCompare = useAppSelector(
    (state) => state.systems.systemsToCompare
  );

  const alternativesFor = useCallback(
    (condenserUniqueId: string) => {
      const current = option.configuration.condensers.find(
        (condenser) => condenser.uniqueId === condenserUniqueId
      );
      if (!current) return [];
      return otherPtacUnitSizes(
        alternatives?.[condenserUniqueId] ?? [],
        current.selectedProduct.productId
      );
    },
    [alternatives, option]
  );

  const swap = useCallback(
    (condenserUniqueId: string, productId: number) => {
      const current = option.configuration.condensers.find(
        (condenser) => condenser.uniqueId === condenserUniqueId
      );
      if (
        !current ||
        !unitPicks ||
        current.selectedProduct.productId === productId
      ) {
        return;
      }
      const selection = buildPtacCustomSelection({
        id: ptacCustomSelectionId(),
        name: resolveCustomSystemName(
          option,
          ptacCustomSystemNames([...ptacOptions, ...systemsToCompare])
        ),
        unitPicks,
        condenserUniqueId,
        productId
      });
      if (isPtacCustomOption(option)) {
        dispatch(removePtacCustomSelection(option.id));
      }
      dispatch(addPtacCustomSelection(selection));
      dispatch(
        bumpCustomSystemsRevision({ configId: option.configuration.uniqueId })
      );
      onCustomSystemSaved?.(selection.id);
    },
    [
      option,
      unitPicks,
      ptacOptions,
      systemsToCompare,
      dispatch,
      onCustomSystemSaved
    ]
  );

  return { alternativesFor, swap };
}
